import { useEffect, useRef, useState } from "react";
import api, { streamPost } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Markdown } from "@/lib/markdown";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Send, Plus, History, Sparkles, Loader2, Wrench, Lock, Crown, MessageSquare, Target } from "lucide-react";

const MODES = [
  { id: "beginner", label: "Beginner" },
  { id: "intermediate", label: "Intermediate" },
  { id: "advanced", label: "Advanced" },
];

const SUGGESTIONS = [
  "Explain CAC and LTV like I'm new to business",
  "How do I find product-market fit?",
  "What is a SAFE and how does it work?",
  "Give me a go-to-market plan for a SaaS app",
];

const TOOLS = [
  { id: "analyze_idea", label: "Analyze a startup idea", placeholder: "Describe your startup idea…", pro: false },
  { id: "business_plan", label: "Generate a business plan", placeholder: "Describe the business…", pro: true },
  { id: "gtm", label: "Go-to-market strategy", placeholder: "Describe the product & target market…", pro: true },
];

function newId() {
  return (crypto.randomUUID && crypto.randomUUID()) || `s_${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

export default function Mentor() {
  const { user } = useAuth();
  const isPro = user?.is_pro || user?.tier === "pro";
  const [sessionId, setSessionId] = useState(newId());
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [mode, setMode] = useState("beginner");
  const [streaming, setStreaming] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [usage, setUsage] = useState(null);
  const scrollRef = useRef(null);

  const loadSessions = () => api.get("/mentor/sessions").then((r) => r.data).catch(() => []);
  const loadUsage = () => api.get("/mentor/usage").then((r) => setUsage(r.data)).catch(() => {});

  // Resume the most recent conversation on mount (persistent memory across logins)
  useEffect(() => {
    (async () => {
      const s = await loadSessions();
      setSessions(s);
      if (s && s.length) {
        const latest = s[0].session_id;
        setSessionId(latest);
        const { data } = await api.get(`/mentor/messages?session_id=${latest}`);
        setMessages(data);
      }
    })();
    loadUsage();
  }, []);

  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [messages]);

  const refreshSessions = async () => setSessions(await loadSessions());

  const openSession = async (sid) => {
    setSessionId(sid);
    const { data } = await api.get(`/mentor/messages?session_id=${sid}`);
    setMessages(data);
  };

  const startNew = () => { setSessionId(newId()); setMessages([]); };

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      await streamPost("/mentor/chat", { session_id: sessionId, message: content, mode }, (chunk) => {
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + chunk };
          return copy;
        });
      });
      refreshSessions(); loadUsage();
    } catch (err) {
      if (err.status === 402) {
        setMessages((m) => m.slice(0, -1));
        toast.error(err.message, { description: "Upgrade to ECO Pro for unlimited mentoring." });
      } else {
        setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: "_Something went wrong. Please try again._" }; return c; });
      }
    } finally {
      setStreaming(false);
    }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-160px)] md:h-[calc(100vh-130px)]" data-testid="mentor-page">
      {/* Header row */}
      <div className="flex items-center gap-2 mb-4">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <div className="h-9 w-9 rounded-xl bg-[#0055FF]/15 border border-[#0055FF]/30 flex items-center justify-center shrink-0">
            <Sparkles size={18} className="text-[#4d8bff]" />
          </div>
          <div className="min-w-0">
            <h1 className="font-display text-lg font-semibold text-white leading-none">ECO Mentor</h1>
            <p className="text-[11px] text-slate-500 mt-0.5">
              {usage && !usage.is_pro ? `${usage.used}/${usage.limit} free messages today` : "Unlimited · Pro"}
            </p>
          </div>
        </div>
        <div className="hidden sm:flex items-center bg-white/5 border border-white/10 rounded-full p-1">
          {MODES.map((m) => (
            <button key={m.id} data-testid={`mode-${m.id}`} onClick={() => setMode(m.id)}
              className={`text-xs font-medium px-3 py-1.5 rounded-full transition-all ${mode === m.id ? "bg-white text-black" : "text-slate-400 hover:text-white"}`}>
              {m.label}
            </button>
          ))}
        </div>
        <GoalsDialog />
        <ToolsDialog isPro={isPro} />
        <Sheet onOpenChange={(o) => o && refreshSessions()}>
          <SheetTrigger asChild>
            <button data-testid="mentor-history-btn" className="btn-secondary text-sm py-2 px-3"><History size={16} /></button>
          </SheetTrigger>
          <SheetContent side="right" className="bg-[#0A0A0A] border-white/10 text-white w-[320px]">
            <SheetHeader><SheetTitle className="text-white font-display">Chat history</SheetTitle></SheetHeader>
            <button onClick={startNew} data-testid="mentor-newchat-btn" className="btn-secondary w-full text-sm mt-4"><Plus size={15} /> New chat</button>
            <div className="mt-4 space-y-2 overflow-y-auto max-h-[calc(100vh-180px)] no-scrollbar">
              {sessions.length === 0 && <p className="text-sm text-slate-500 text-center py-8">No conversations yet.</p>}
              {sessions.map((s) => (
                <button key={s.session_id} onClick={() => openSession(s.session_id)} data-testid={`session-${s.session_id}`}
                  className={`w-full text-left p-3 rounded-xl border transition-colors flex items-start gap-2 ${s.session_id === sessionId ? "bg-white/10 border-white/20" : "border-white/5 hover:bg-white/5"}`}>
                  <MessageSquare size={15} className="text-slate-500 mt-0.5 shrink-0" />
                  <span className="text-sm text-slate-300 line-clamp-2">{s.title}</span>
                </button>
              ))}
            </div>
          </SheetContent>
        </Sheet>
        <button onClick={startNew} className="btn-primary text-sm py-2 px-3 hidden md:inline-flex" data-testid="mentor-newchat-top"><Plus size={16} /></button>
      </div>

      <div className="sm:hidden flex items-center bg-white/5 border border-white/10 rounded-full p-1 mb-3 self-start">
        {MODES.map((m) => (
          <button key={m.id} data-testid={`mode-m-${m.id}`} onClick={() => setMode(m.id)}
            className={`text-xs font-medium px-3 py-1.5 rounded-full transition-all ${mode === m.id ? "bg-white text-black" : "text-slate-400"}`}>{m.label}</button>
        ))}
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto no-scrollbar space-y-4 pr-1">
        {messages.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-center px-4">
            <div className="h-14 w-14 rounded-2xl bg-[#0055FF]/15 border border-[#0055FF]/30 flex items-center justify-center mb-5">
              <Sparkles size={26} className="text-[#4d8bff]" />
            </div>
            <h2 className="font-display text-2xl font-semibold text-white">Ask ECO anything about business.</h2>
            <p className="text-slate-400 mt-2 max-w-md">From startups to negotiation to finance — your AI mentor explains it at your level and remembers your goals.</p>
            <div className="grid sm:grid-cols-2 gap-3 mt-8 w-full max-w-2xl">
              {SUGGESTIONS.map((s) => (
                <button key={s} data-testid="mentor-suggestion" onClick={() => send(s)} className="eco-card eco-card-hover p-4 text-left text-sm text-slate-300">{s}</button>
              ))}
            </div>
          </div>
        ) : (
          messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.role}`}>
              {m.role === "user" ? (
                <div className="bg-white/10 text-white rounded-2xl rounded-tr-sm px-4 py-3 max-w-[85%]">{m.content}</div>
              ) : (
                <div className="bg-transparent border border-white/5 rounded-2xl rounded-tl-sm px-4 py-3 max-w-[92%] w-full">
                  {m.content ? <Markdown>{m.content}</Markdown> : <span className="stream-caret text-slate-500" />}
                  {streaming && i === messages.length - 1 && m.content && <span className="stream-caret" />}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      <div className="mt-4">
        <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-end gap-2">
          <textarea
            data-testid="mentor-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            rows={1}
            placeholder="Message ECO…"
            className="flex-1 resize-none bg-[#0A0A0A] border border-white/10 rounded-2xl px-4 py-3 text-white placeholder:text-slate-500 focus:outline-none focus:border-white/25 max-h-32"
          />
          <button type="submit" data-testid="mentor-send-btn" disabled={streaming || !input.trim()} className="btn-accent h-12 w-12 !px-0 rounded-2xl shrink-0">
            {streaming ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
          </button>
        </form>
      </div>
    </div>
  );
}

function GoalsDialog() {
  const [open, setOpen] = useState(false);
  const [goals, setGoals] = useState("");
  const [industry, setIndustry] = useState("");
  const [experience, setExperience] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/coach/goals"); setGoals(data.goals || ""); setIndustry(data.industry || ""); setExperience(data.experience || ""); } catch (e) {}
  };

  const save = async () => {
    setLoading(true);
    try { await api.post("/coach/goals", { goals, industry, experience }); toast.success("ECO will remember your goals"); setOpen(false); }
    catch (e) { toast.error("Could not save"); } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (o) load(); }}>
      <DialogTrigger asChild>
        <button data-testid="mentor-goals-btn" className="btn-secondary text-sm py-2 px-3"><Target size={16} /></button>
      </DialogTrigger>
      <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-lg">
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Target size={18} className="text-[#4d8bff]" /> Your goals (ECO remembers these)</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-400">ECO personalizes its advice and recommendations to your long-term goals.</p>
        <textarea data-testid="goals-input" value={goals} onChange={(e) => setGoals(e.target.value)} rows={3} placeholder="e.g., I want to build and launch a profitable AI SaaS in the next 12 months." className="eco-input resize-none" />
        <div className="grid grid-cols-2 gap-3">
          <input data-testid="goals-industry" value={industry} onChange={(e) => setIndustry(e.target.value)} placeholder="Industry" className="eco-input" />
          <select data-testid="goals-experience" value={experience} onChange={(e) => setExperience(e.target.value)} className="eco-input">
            <option value="" className="bg-[#0A0A0A]">Experience…</option>
            {["Beginner", "Intermediate", "Advanced"].map((x) => <option key={x} value={x} className="bg-[#0A0A0A]">{x}</option>)}
          </select>
        </div>
        <button data-testid="goals-save-btn" onClick={save} disabled={loading} className="btn-accent w-full">{loading ? <Loader2 size={16} className="animate-spin" /> : "Save goals"}</button>
      </DialogContent>
    </Dialog>
  );
}

function ToolsDialog({ isPro }) {
  const [open, setOpen] = useState(false);
  const [tool, setTool] = useState(TOOLS[0]);
  const [input, setInput] = useState("");
  const [result, setResult] = useState("");
  const [loading, setLoading] = useState(false);

  const run = async () => {
    if (!input.trim()) return;
    if (tool.pro && !isPro) { toast.error("This tool requires ECO Pro."); return; }
    setLoading(true); setResult("");
    try {
      const { data } = await api.post("/tools/run", { tool: tool.id, input });
      setResult(data.markdown);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to generate");
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid="mentor-tools-btn" className="btn-secondary text-sm py-2 px-3"><Wrench size={16} /></button>
      </DialogTrigger>
      <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Wrench size={18} className="text-[#4d8bff]" /> ECO Tools</DialogTitle></DialogHeader>
        <div className="flex flex-wrap gap-2">
          {TOOLS.map((t) => (
            <button key={t.id} data-testid={`tool-${t.id}`} onClick={() => { setTool(t); setResult(""); }}
              className={`text-xs font-medium px-3 py-2 rounded-full border transition-all flex items-center gap-1.5 ${tool.id === t.id ? "bg-white text-black border-white" : "border-white/10 text-slate-300 hover:bg-white/5"}`}>
              {t.label} {t.pro && <Crown size={12} className={tool.id === t.id ? "text-amber-600" : "text-amber-400"} />}
            </button>
          ))}
        </div>
        {tool.pro && !isPro ? (
          <div className="eco-card p-6 text-center mt-2">
            <Lock size={24} className="text-amber-400 mx-auto mb-3" />
            <p className="text-slate-300 text-sm">This tool is part of ECO Pro.</p>
            <a href="/pricing" className="btn-accent text-sm mt-4 inline-flex">Upgrade to Pro</a>
          </div>
        ) : (
          <>
            <textarea data-testid="tool-input" value={input} onChange={(e) => setInput(e.target.value)} rows={3} placeholder={tool.placeholder} className="eco-input resize-none" />
            <button data-testid="tool-run-btn" onClick={run} disabled={loading || !input.trim()} className="btn-accent w-full">
              {loading ? <Loader2 size={16} className="animate-spin" /> : <><Sparkles size={16} /> Generate</>}
            </button>
            {result && <div className="eco-card p-5 mt-2" data-testid="tool-result"><Markdown>{result}</Markdown></div>}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
